/**
 * Type Compatibility Matrix
 * Defines which types can be connected to which other types
 */

import { SignalType, DataType, EnhancedNodeType, TypeUtils } from '../types/enhanced-types';

// Core compatibility rules
export const TYPE_COMPATIBILITY_MATRIX: Record<EnhancedNodeType, EnhancedNodeType[]> = {
  // SIGNAL Types - nur untereinander kompatibel
  [SignalType.TRIGGER]: [
    SignalType.TRIGGER,
    SignalType.SIGNAL_ANY
  ],
  
  [SignalType.EVENT]: [
    SignalType.EVENT,
    SignalType.SIGNAL_ANY
  ],
  
  [SignalType.FLOW]: [
    SignalType.FLOW,
    SignalType.SIGNAL_ANY
  ],

  [SignalType.ERROR]: [
    SignalType.ERROR,
    SignalType.SIGNAL_ANY
  ],
  
  [SignalType.SIGNAL_ANY]: [
    SignalType.TRIGGER,
    SignalType.EVENT,
    SignalType.FLOW,
    SignalType.ERROR
  ],

  // DATA Types - untereinander + mit DATA_ANY kompatibel
  [DataType.TEXT]: [
    DataType.TEXT,
    DataType.DATA_ANY
  ],
  
  [DataType.NUMBER]: [
    DataType.NUMBER,
    DataType.TEXT,        // Numbers can convert to text
    DataType.DATA_ANY
  ],
  
  [DataType.BOOLEAN]: [
    DataType.BOOLEAN,
    DataType.TEXT,        // Booleans can convert to text
    DataType.NUMBER,      // Booleans can convert to 0/1
    DataType.DATA_ANY
  ],
  
  [DataType.IMAGE]: [
    DataType.IMAGE,
    DataType.FILE,        // Images are files
    DataType.URL,         // Images can have URLs
    DataType.DATA_ANY
  ],
  
  [DataType.OBJECT]: [
    DataType.OBJECT,
    DataType.JSON,        // Objects can serialize to JSON
    DataType.DATA_ANY
  ],
  
  [DataType.ARRAY]: [
    DataType.ARRAY,
    DataType.JSON,        // Arrays can serialize to JSON
    DataType.TEXT,        // Arrays can convert to text representation
    DataType.DATA_ANY
  ],
  
  [DataType.FILE]: [
    DataType.FILE,
    DataType.URL,         // Files can have URLs
    DataType.TEXT,        // File paths are text
    DataType.DATA_ANY
  ],
  
  [DataType.URL]: [
    DataType.URL,
    DataType.TEXT,        // URLs are text strings
    DataType.DATA_ANY
  ],
  
  [DataType.JSON]: [
    DataType.JSON,
    DataType.TEXT,        // JSON is text
    DataType.OBJECT,      // JSON can parse to objects
    DataType.ARRAY,       // JSON can parse to arrays
    DataType.DATA_ANY
  ],

  [DataType.ERROR]: [
    DataType.ERROR,
    DataType.OBJECT,      // Error data is structured object
    DataType.JSON,        // Error can serialize to JSON
    DataType.TEXT,        // Error can convert to text message
    DataType.DATA_ANY
  ],

  [DataType.FILE_PATH]: [
    DataType.FILE_PATH,
    DataType.TEXT,        // File paths are text strings
    DataType.URL,         // File paths can be URLs
    DataType.FILE,        // File paths reference files
    DataType.DATA_ANY
  ],
  
  [DataType.DATA_ANY]: [
    DataType.TEXT,
    DataType.NUMBER,
    DataType.BOOLEAN,
    DataType.IMAGE,
    DataType.OBJECT,
    DataType.ARRAY,
    DataType.FILE,
    DataType.URL,
    DataType.JSON,
    DataType.ERROR,
    DataType.FILE_PATH
  ]
};

// Type conversion confidence levels
export interface TypeConversion {
  from: EnhancedNodeType;
  to: EnhancedNodeType;
  confidence: 'high' | 'medium' | 'low';
  lossy: boolean;
  description: string;
}

export const TYPE_CONVERSIONS: TypeConversion[] = [
  // High confidence conversions
  {
    from: DataType.NUMBER,
    to: DataType.TEXT,
    confidence: 'high',
    lossy: false,
    description: 'Numbers convert reliably to text'
  },
  {
    from: DataType.BOOLEAN,
    to: DataType.TEXT,
    confidence: 'high', 
    lossy: false,
    description: 'Booleans convert to "true"/"false"'
  },
  {
    from: DataType.URL,
    to: DataType.TEXT,
    confidence: 'high',
    lossy: false,
    description: 'URLs are text strings'
  },
  {
    from: DataType.JSON,
    to: DataType.TEXT,
    confidence: 'high',
    lossy: false,
    description: 'JSON is formatted text'
  },
  
  // Medium confidence conversions
  {
    from: DataType.TEXT,
    to: DataType.NUMBER,
    confidence: 'medium',
    lossy: true,
    description: 'Text may contain numeric values'
  },
  {
    from: DataType.TEXT,
    to: DataType.BOOLEAN,
    confidence: 'medium',
    lossy: true,
    description: 'Text may represent boolean values'
  },
  {
    from: DataType.OBJECT,
    to: DataType.JSON,
    confidence: 'medium',
    lossy: false,
    description: 'Objects can serialize to JSON'
  },
  {
    from: DataType.ARRAY,
    to: DataType.JSON,
    confidence: 'medium',
    lossy: false,
    description: 'Arrays can serialize to JSON'
  },
  
  // Error type conversions - High confidence
  {
    from: DataType.ERROR,
    to: DataType.TEXT,
    confidence: 'high',
    lossy: true,
    description: 'Error converts to error message text'
  },
  {
    from: DataType.ERROR,
    to: DataType.OBJECT,
    confidence: 'high',
    lossy: false,
    description: 'Error data is a structured object'
  },
  {
    from: DataType.ERROR,
    to: DataType.JSON,
    confidence: 'high',
    lossy: false,
    description: 'Error data serializes to JSON'
  },

  // File path conversions - High confidence
  {
    from: DataType.FILE_PATH,
    to: DataType.TEXT,
    confidence: 'high',
    lossy: false,
    description: 'File paths are text strings'
  },
  {
    from: DataType.FILE_PATH,
    to: DataType.URL,
    confidence: 'medium',
    lossy: false,
    description: 'File paths can be file:// URLs'
  },

  // Low confidence conversions
  {
    from: DataType.JSON,
    to: DataType.OBJECT,
    confidence: 'low',
    lossy: true,
    description: 'JSON may not be a valid object'
  },
  {
    from: DataType.JSON,
    to: DataType.ARRAY,
    confidence: 'low',
    lossy: true,
    description: 'JSON may not be a valid array'
  }
];

/**
 * Utility class for type compatibility checking
 */
export class TypeCompatibilityChecker {
  /**
   * Check if two types are directly compatible
   */
  static isDirectlyCompatible(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): boolean {
    const compatibleTypes = TYPE_COMPATIBILITY_MATRIX[sourceType];
    return compatibleTypes?.includes(targetType) ?? false;
  }

  /**
   * Check if types are compatible (including cross-category rules)
   */
  static isCompatible(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): boolean {
    // Same type is always compatible
    if (sourceType === targetType) return true;
    
    // Check direct compatibility
    if (this.isDirectlyCompatible(sourceType, targetType)) return true;
    
    // SIGNAL types are only compatible within SIGNAL category
    if (TypeUtils.isSignalType(sourceType) && TypeUtils.isDataType(targetType)) return false;
    if (TypeUtils.isDataType(sourceType) && TypeUtils.isSignalType(targetType)) return false;
    
    return false;
  }

  /**
   * Get all compatible types for a given source type
   */
  static getCompatibleTypes(sourceType: EnhancedNodeType): EnhancedNodeType[] {
    return TYPE_COMPATIBILITY_MATRIX[sourceType] || [];
  }

  /**
   * Get type conversion info if available
   */
  static getTypeConversion(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): TypeConversion | null {
    return TYPE_CONVERSIONS.find(
      conversion => conversion.from === sourceType && conversion.to === targetType
    ) || null;
  }

  /**
   * Check if automatic conversion is possible
   */
  static canAutoConvert(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): boolean {
    const conversion = this.getTypeConversion(sourceType, targetType);
    return conversion !== null && conversion.confidence !== 'low';
  }

  /**
   * Get compatibility category for validation result
   */
  static getCompatibilityCategory(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): 'valid' | 'warning' | 'invalid' {
    if (sourceType === targetType) return 'valid';
    if (this.isDirectlyCompatible(sourceType, targetType)) return 'valid';
    
    const conversion = this.getTypeConversion(sourceType, targetType);
    if (conversion) {
      if (conversion.confidence === 'high') return 'valid';
      if (conversion.confidence === 'medium') return 'warning';
      return 'invalid';
    }
    
    return 'invalid';
  }

  /**
   * Get human-readable compatibility message
   */
  static getCompatibilityMessage(sourceType: EnhancedNodeType, targetType: EnhancedNodeType): string {
    const sourceDisplay = TypeUtils.getTypeDisplayName(sourceType);
    const targetDisplay = TypeUtils.getTypeDisplayName(targetType);
    
    if (sourceType === targetType) {
      return `${sourceDisplay} types are perfectly compatible`;
    }
    
    if (this.isDirectlyCompatible(sourceType, targetType)) {
      return `${sourceDisplay} can connect to ${targetDisplay}`;
    }
    
    const conversion = this.getTypeConversion(sourceType, targetType);
    if (conversion) {
      return `${sourceDisplay} → ${targetDisplay}: ${conversion.description}`;
    }
    
    // Check category mismatch
    if (!TypeUtils.sameCategory(sourceType, targetType)) {
      const sourceCategory = TypeUtils.getTypeCategory(sourceType);
      const targetCategory = TypeUtils.getTypeCategory(targetType);
      return `Cannot connect ${sourceCategory} types to ${targetCategory} types`;
    }
    
    return `${sourceDisplay} is not compatible with ${targetDisplay}`;
  }
}

export default {
  TYPE_COMPATIBILITY_MATRIX,
  TYPE_CONVERSIONS,
  TypeCompatibilityChecker
};